/**
 * The pinned notation nav row (fix D / R1 + R6.6).
 *
 * It used to live INSIDE `MoveList/View.tsx`, which meant it disappeared with
 * the notation the moment the rail showed another tab. The rail's tabs make that
 * wrong: a player reading the standings still wants ⏮ ◀ ▶ ⏭, so the row is a
 * sibling of the body, pinned above the action bar.
 *
 * It reads the SAME two numbers the MoveList's own model derives (which ply is
 * on the board, and how many there are) through the same `plyCount` helper, so
 * the two cannot clamp against different lengths. `goTo` is `MoveList`'s rule
 * verbatim: it reads the live length at PRESS time instead of closing over it.
 *
 * FIX F-C: ◀ ▶ step the analysis branch exactly like ← → do in `MoveList`'s
 * own keyboard handler (`step`, built on the same exported `stepBranch`); ⏮ ⏭
 * stay plain jumps — `goTo` clears the branch first, same as a notation click.
 *
 * REVIEW FIX (2026-10-06): ◀ / ▶'s own `disabled` used to read `current`,
 * which is PINNED to the branch's `basePly` while one is open (so the
 * main-line highlight stays put) — never the branch's own cursor. A branch
 * opened at the live end (`basePly === total`) therefore showed ▶ disabled
 * from the moment it opened, and stayed that way forever regardless of how
 * far back the cursor stepped; a branch from ply 0 showed ◀ disabled from
 * the start, even once the cursor had moved past it. `atStart` / `atEnd`
 * below are the CURSOR's own bounds while a branch is open, so the two
 * buttons track what stepping would actually do.
 */
import {useCallback} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import {
  selectBoardSource,
  selectMoves,
  selectWatchMoves,
} from '@/Store/liveGame/selectors';

import {useV2Ui} from '../../../theme/useV2Ui';
import {useAnalysis} from '../../analysis/analysisStore';
import {plyCount, stepBranch} from '../MoveList/model';

export type MoveNavModel = {
  /** how many plies are on the board right now — the branch's `basePly`
   * while one is open, matching `MoveList`'s own `current` */
  current: number;
  total: number;
  /** ◀'s own bound: the branch's cursor at its root, or the main line at 0 */
  atStart: boolean;
  /** ▶'s own bound: the branch's cursor at its tip, or the main line at the end */
  atEnd: boolean;
  goTo: (ply: number) => void;
  /** ◀ / ▶: branch-aware step (F-C / C5) */
  step: (delta: -1 | 1) => void;
};

export function useMoveNavModel(): MoveNavModel {
  const source = useLiveGame(selectBoardSource);
  const moves = useLiveGame(
    source === 'watch' ? selectWatchMoves : selectMoves,
  );
  // the WHOLE branch, not just `basePly` (review fix): the cursor has to move
  // this hook too, or ◀ / ▶'s bounds below go stale the moment it steps
  const branch = useAnalysis(useShallow(s => s.branch));
  const viewPly = useV2Ui(s => s.viewPly);
  const setViewPly = useV2Ui(s => s.setViewPly);

  const goTo = useCallback(
    (ply: number) => {
      // ⏮ / ⏭ (and a notation click) clear the branch first, like MoveList's
      if (useAnalysis.getState().branch) {
        useAnalysis.getState().clearBranch();
      }
      const total = plyCount(useLiveGame.getState());
      const clamped = ply < 0 ? 0 : ply > total ? total : ply;
      setViewPly(clamped >= total ? null : clamped);
    },
    [setViewPly],
  );

  const step = useCallback(
    (delta: -1 | 1) => {
      if (stepBranch(delta, goTo)) {
        return;
      }
      const total = plyCount(useLiveGame.getState());
      const at = useV2Ui.getState().viewPly ?? total;
      goTo(at + delta);
    },
    [goTo],
  );

  const total = moves.length;
  const current = branch ? branch.basePly : viewPly === null ? total : viewPly;

  return {
    current,
    total,
    // review fix: the branch's OWN cursor bounds while one is open — ⏮ / ⏭
    // keep reading `current` / `total` (the main line), unchanged
    atStart: branch ? branch.cursor === 0 : current === 0,
    atEnd: branch ? branch.cursor === branch.moves.length : current >= total,
    goTo,
    step,
  };
}

export default useMoveNavModel;
